/**
 * Risk-Profile questionnaire — single source of truth.
 * Used by CreateRiskProfile (interactive form) and blank-PDF generator.
 * Each option carries its own `points` value used for scoring.
 */
export const RISK_QUESTIONS = [
  {
    key: 'q1_age_group',
    question: 'How old are you?',
    options: [
      { label: 'Below 25 years', points: 10 },
      { label: '25-35 years', points: 8 },
      { label: '36-45 years', points: 6 },
      { label: '46-55 years', points: 4 },
      { label: 'Above 55 years', points: 2 },
    ],
  },
  {
    key: 'q2_investment_horizon',
    question: 'How many years away is your nearest financial goal?',
    options: [
      { label: 'Less than 1 year', points: 2 },
      { label: '1-3 years', points: 4 },
      { label: '3-5 years', points: 6 },
      { label: '5-10 years', points: 8 },
      { label: 'More than 10 years', points: 10 },
    ],
  },
  {
    key: 'q3_poor_performance_hold',
    question:
      'If your investment outlook is long-term (more than 5 years), how long will you hold on to a poorly performing portfolio before cashing in?',
    options: [
      { label: 'Cash in immediately', points: 2 },
      { label: '6 months', points: 4 },
      { label: '1 year', points: 6 },
      { label: '2 years', points: 8 },
      { label: 'Hold for long-term despite fluctuations', points: 10 },
    ],
  },
  {
    key: 'q4_income_source',
    question: 'My current and future source of income are:',
    options: [
      { label: 'Pension/Fixed income only', points: 2 },
      { label: 'Stable salary', points: 4 },
      { label: 'Salary with performance bonus', points: 6 },
      { label: 'Business/Self-employed', points: 8 },
      { label: 'Multiple sources of income', points: 10 },
    ],
  },
  {
    key: 'q5_investment_objective',
    question: 'Choose your investment preference/Objective:',
    options: [
      { label: 'Capital Preservation (Safety first)', points: 2 },
      { label: 'Regular Income generation', points: 4 },
      { label: 'Balanced (Income + Growth)', points: 6 },
      { label: 'Capital Growth', points: 8 },
      { label: 'Aggressive Growth (Maximum returns)', points: 10 },
    ],
  },
  {
    key: 'q6_risk_comfort',
    question: 'How comfortable are you with investment risk?',
    options: [
      { label: 'Very uncomfortable - prefer guaranteed returns', points: 2 },
      { label: 'Somewhat uncomfortable', points: 4 },
      { label: 'Neutral', points: 6 },
      { label: 'Comfortable with moderate risk', points: 8 },
      { label: 'Very comfortable - willing to take high risk', points: 10 },
    ],
  },
  {
    key: 'q7_investment_experience',
    question: 'What is your investment experience?',
    options: [
      { label: 'No experience', points: 2 },
      { label: 'Limited (Less than 2 years)', points: 4 },
      { label: 'Moderate (2-5 years)', points: 6 },
      { label: 'Experienced (5-10 years)', points: 8 },
      { label: 'Very experienced (More than 10 years)', points: 10 },
    ],
  },
  {
    key: 'q8_market_volatility',
    question: 'If your investment loses 20% of its value in a short period, you would:',
    options: [
      { label: 'Sell all investments immediately', points: 2 },
      { label: 'Sell some to cut losses', points: 4 },
      { label: 'Hold and wait for recovery', points: 6 },
      { label: 'Buy more at lower prices', points: 10 },
    ],
  },
  {
    key: 'q9_liquidity_needs',
    question: 'When might you need this invested money?',
    options: [
      { label: 'May need anytime (high liquidity)', points: 2 },
      { label: 'Within 1 year', points: 4 },
      { label: '1-3 years', points: 6 },
      { label: '3-5 years', points: 8 },
      { label: 'Not needed for 5+ years', points: 10 },
    ],
  },
  {
    key: 'q10_portfolio_preference',
    question: 'What would be your ideal portfolio allocation?',
    options: [
      { label: '100% Fixed Deposits/Bonds', points: 1 },
      { label: '80% Fixed Income, 20% Equity', points: 3 },
      { label: '50% Fixed Income, 50% Equity', points: 5 },
      { label: '20% Fixed Income, 80% Equity', points: 7 },
      { label: '100% Equity', points: 10 },
    ],
  },
];

/**
 * Risk-profile band thresholds. Total score is the sum of all option points.
 * Max possible = 10*9 + 10 (Q8 max 10) = actual theoretical max is ~100.
 */
export const RISK_BANDS = [
  { label: 'Conservative', min: 0, max: 40, description: 'Focus on capital preservation and steady returns. Low tolerance for volatility.' },
  { label: 'Moderate', min: 41, max: 70, description: 'Balanced approach between growth and safety. Tolerates moderate volatility for higher returns.' },
  { label: 'Aggressive', min: 71, max: 100, description: 'Prioritises high returns; comfortable with significant volatility over a long horizon.' },
];
